import InfiniteScroll from 'react-infinite-scroll-component';
import { Role, rolesManipulator } from 'auth';
import { useContext, useEffect, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { MeetingItem } from '@/modules/common/components/items/MeetingItem.tsx';
import { AddUpdateMeetingModal } from '@/modules/common/components/modals/AddUpdateMeetingModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useGetMeetingsInfiniteQuery } from '@/modules/common/queries/meetings.ts';
import {
  Button,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  Spinner,
  TooltipNew,
} from 'ui';

const Meetings = () => {
  const userRole = rolesManipulator.current_user_role;
  const isAdmin = userRole === Role.COMPLIANCE_ADMIN;
  const businessId = useGetCurrentBusinessId();
  const { openModal } = useContext(ModalProviderContext);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue('');
  }, [businessId]);

  const {
    hasNextPage,
    fetchNextPage,
    data: meetingsData,
    isLoading,
  } = useGetMeetingsInfiniteQuery({
    page: 1,
    page_size: 18,
    search: searchValue,
  });

  const meetings = (meetingsData?.pages || []).flatMap(({ data }) => data);

  return (
    <PageLayout
      title="Meetings"
      headerAction={
        <div className="flex gap-4 items-center">
          <SearchField
            className="!w-[240px]"
            placeholder="Search"
            value={searchValue}
            onChange={setSearchValue}
          />
          <div className="w-fit">
            <TooltipNew
              text="Only users with Admin role can schedule new meetings."
              disabled={isAdmin}
            >
              <Button
                onClick={() => openModal(AddUpdateMeetingModal)}
                variant="primary"
                disabled={!isAdmin}
              >
                Schedule Meeting
              </Button>
            </TooltipNew>
          </div>
        </div>
      }
    >
      <EmptyListState
        title="No meetings created yet"
        description="You can use this feature to create Microsoft Teams meetings and Calendar records for online and offline meetings."
        isLoading={isLoading}
        isSearchUsed={!!searchValue}
        itemsCount={meetings.length}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      {!!meetings.length && (
        <InfiniteScroll
          next={fetchNextPage}
          dataLength={meetings.length}
          hasMore={!!hasNextPage}
          className="w-full flex flex-col space-y-2"
          style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          {meetings.map(meeting => (
            <MeetingItem key={meeting.id} meeting={meeting} />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(Meetings, [Role.COMPLIANCE_ADMIN, Role.COMPLIANCE_USER]);
